// import node module libraries
import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Row, Col, Button, Dropdown, Badge, Table, Offcanvas, Form } from 'react-bootstrap';
import { MoreVertical, Edit, Trash } from 'react-feather';
import Moment from 'react-moment';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import required custom components
import TanstackTable from 'components/elements/advance-table/TanstackTable';
// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Import the new components
import OffCanvasUpdateFAQ from './OffcanvasUpdateFAQ';
import ModalDeleteFAQ from './ModalDeleteFAQ';

const FAQSettingsTable = ({ activeKey, faqdata, isLoadingTable, filterBy = '' }) => {
	// Define required states
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [selectedRow, setSelectedRow] = useState({});
	const [isLoadingButtonUpdateFAQ, setIsLoadingButtonUpdateFAQ] = useState(null); // State for loading spinner

	// Methods / Functions
	const handleRowClick = (row, action) => {
		if (action === 'open') {
			setShowUpdateFAQOffcanvas(true);
			fetchRowDetails(row); // Call API to fetch details
		} else if (action === 'delete') {
			handleDeleteClick(row);
			setShowUpdateFAQOffcanvas(false);
		}
	};

	// Delete FAQ
	const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing modal
	const [rowToDelete, setRowToDelete] = useState(null); // State for API Credential to delete
	const [isLoadingButtonDeleteFAQ, setIsLoadingButtonDeleteFAQ] = useState(null); // State for loading spinner

	const handleDeleteClick = (faq) => {
		setRowToDelete(faq);
		setShowDeleteModal(true);
	};

	// fetch data from api
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	const handleConfirmDelete = async () => {
		if (rowToDelete) {
			setIsLoadingButtonDeleteFAQ(true);
			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			await axios.delete(`${baseURL}/public/faqs/${rowToDelete}`, options)
				.then(response => {
					// Update the faqdata state with the deleted row
					const updatedData = faqdata.data.filter((row) => row.id !== rowToDelete);
					faqdata.data = updatedData;
					// Update the faqdata state with the deleted data
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteFAQ(false);
					handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
				})
				.catch((error) => {
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteFAQ(false);
					const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
					handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				});
		}
	};

	const fetchRowDetails = async (rowId) => {
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		try {
			const response = await axios.get(`${baseURL}/public/faqs/${rowId}`, options);
			const rowDetails = response.data;
			setSelectedRow(rowDetails.data); // Update selectedRow state with fetched log details

		} catch (error) {
			console.error(error);
		}
	};

	// Update FAQ
	const [showUpdateFAQOffcanvas, setShowUpdateFAQOffcanvas] = useState(false);
	const handleShowUpdateFAQ = () => setShowUpdateFAQOffcanvas(true);
	const handleCloseUpdateFAQ = () => {
		setShowUpdateFAQOffcanvas(false);
		setSelectedRow({});
	};

	const handleUpdateFAQ = async (e) => {
		e.preventDefault();
		setIsLoadingButtonUpdateFAQ(true);

		const data = {
			faq_title: e.target.title.value,
			faq_content: e.target.content.value,
			faq_categories: 
				e.target.categories.value.split(',').map((category) => [(category.trim())])
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/public/faqs/${e.target.id.value}`, data, options)
			.then(response => {
				// Update the selected faq in the data state
				const updatedRow = response.data;
				setSelectedRow(updatedRow);
				// Update the faqdata state with the updated row
				const updatedData = faqdata.data.map((row) => (row.id === updatedRow.result.id ? 
					{
						id: updatedRow.result.id,
						faq_title: updatedRow.result.faq_title,
						faq_content: updatedRow.result.faq_content,
						faq_categories:
							Array.isArray(updatedRow.result.faq_categories)
								? updatedRow.result.faq_categories.join(',')
								: updatedRow.result.faq_categories
					}
				: row));
				faqdata.data = updatedData;
				// Update the faqdata state with the updated data
				// Close the offcanvas
				setShowUpdateFAQOffcanvas(false);
				setIsLoadingButtonUpdateFAQ(false);
				// Reset the form fields
				e.target.reset();
				// Show success notification
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				//setShowUpdateFAQOffcanvas(false);
				setIsLoadingButtonUpdateFAQ(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};
	
	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to="#"
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	// 
	// Components
	//

	// Component ActionMenu
	const ActionMenu = (row) => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle}>
					<MoreVertical size="15px" className="text-secondary" />
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('ACTION')}</Dropdown.Header>
					<Dropdown.Item eventKey="1" onClick={() => handleRowClick(row.ID, 'open')}>
						<Edit size="14px" className="me-1" /> {t('Edit')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="2" onClick={() => handleRowClick(row.ID, 'delete')}>
						<Trash size="14px" className="me-1" /> {t('Delete')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	const columns = useMemo(
		() => [
			{
				accessorKey: 'faq_title',
				header: t('Title'),
				cell: ({ row }) => {
					return (
						<Link className="fw-semibold" onClick={(event) => {
							event.preventDefault();
							handleRowClick(row.original.id, 'open');
						}}>
							{row.original.faq_title.toString()} <i className="fe fe-arrow-right fs-4"></i>
						</Link>
					);
				}
			},
			{
				accessorKey: 'faq_categories',
				header: t('Categories'),
				cell: ({ getValue }) => {
					const categories = getValue().split(',').map((category, index) => (
						<Badge key={index} className="me-1 text-light-secondary bg-secondary">
							{category.trim()}
						</Badge>
					));
					return <div>{categories}</div>;
				}
			},
			{
				accessorKey: 'action',
				header: t('Action'),
				cell: ({ row }) => {
					return <ActionMenu ID={row.original.id} />;
				}
			}
		],
		[]
	);

	const unfilteredData = useMemo(() => faqdata?.data ?? [], [faqdata.data]);
	//const pagination = useMemo(() => response?.pagination ?? [], [response.pagination]);

	// filtering....
	const data = useMemo(() => {
		return filterBy === '' 
			? unfilteredData 
			: unfilteredData?.filter((item) => item.faq_categories?.includes(filterBy));
	}, [filterBy, unfilteredData]);
	

	if (isLoadingTable) {
		return (
			<Table className="text-nowrap">
				<thead >
					<tr>
						<th scope="col">{t('Title')}</th>
						<th scope="col">{t('Categories')}</th>
						<th scope="col">{t('Action')}</th>
					</tr>
				</thead>
				<tbody>
					{Array(10).fill(0).map((_, index) => (
						<tr key={index}>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
						</tr>
					))}
				</tbody>
			</Table>

		);
	}

	return (
		<div>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<TanstackTable
				data={data}
				columns={columns}
				filter={true}
				filterPlaceholder="Suche"
				pagination={true}
				exportButton={false}

			/>
			{/* Update FAQ Offcanvas */}
			{selectedRow && Object.keys(selectedRow).length > 0 && (
				<OffCanvasUpdateFAQ
					show={showUpdateFAQOffcanvas}
					onHide={handleCloseUpdateFAQ}
					selectedRow={selectedRow}
					onSubmit={handleUpdateFAQ}
					isLoadingButton={isLoadingButtonUpdateFAQ}
				/>
			)}
			{/* Delete Log Modal */}
			<ModalDeleteFAQ
				show={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				handleConfirmDelete={handleConfirmDelete}
				isLoadingButton={isLoadingButtonDeleteFAQ}
			/>
		</div>
	);
};

export default FAQSettingsTable;
